import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from './queryKeys';
import {
  fetchChartssList,
  fetchChartsList,
  createChartObservation,
  deleteChartObservation,
} from '../services/observationService';
import {
  getChartObservation,
  deleteWeightApi,
  listPatientWeights,
  listPatientWeightss,
} from '../services/apiService';
import { addWeightApi } from '../services/patientService';

// ============================================================================
// Normalization Helpers
// ============================================================================

export const normalizeWeightResponse = (response) => {
  const payload = response?.data ?? null;
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== 'object') return [];

  const items = payload.weights ?? payload.data ?? payload.items ?? [];
  return Array.isArray(items) ? items : [];
};

export const flattenPropertyPoints = (properties) =>
  (Array.isArray(properties) ? properties : []).flatMap((property) => {
    const points = Array.isArray(property.points)
      ? property.points
      : Array.isArray(property.data)
        ? property.data
        : [];

    return points.map((point) => ({
      value: point.value ?? point.valeur,
      measured_at: point.measured_at ?? point.date,
      property: property.nom ?? property.parametre,
      date: point.measured_at ?? point.date,
    }));
  });

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch master charts catalog / suggestions.
 * Cached for 30 minutes.
 */
export function useChartsCatalog(options = {}) {
  return useQuery({
    queryKey: queryKeys.charts.catalog(),
    queryFn: async ({ signal }) => {
      const res = await fetchChartsList({ signal });
      return Array.isArray(res) ? res : res?.data ?? [];
    },
    staleTime: 30 * 60 * 1000, // 30 minutes
    gcTime: 60 * 60 * 1000,    // 1 hour
    ...options,
  });
}

/**
 * Fetch chart properties for a doctor & patient.
 * Cached for 5 minutes.
 */
export function useChartProperties(doctorId, patientId, options = {}) {
  return useQuery({
    queryKey: queryKeys.charts.properties(doctorId, patientId),
    queryFn: async ({ signal }) => {
      const res = await fetchChartssList(doctorId, patientId, { signal });
      return Array.isArray(res) ? res : res?.data ?? [];
    },
    enabled: Boolean(doctorId && patientId),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000,   // 10 minutes
    ...options,
  });
}

/**
 * Fetch a single chart observation / property detail.
 */
export function useChartProperty(propertyId, doctorId, patientId, options = {}) {
  return useQuery({
    queryKey: queryKeys.charts.property(propertyId, doctorId, patientId),
    queryFn: async ({ signal }) => {
      const res = await getChartObservation(propertyId, doctorId, patientId, { signal });
      return res?.data ?? res;
    },
    enabled: Boolean(propertyId && doctorId && patientId),
    staleTime: 5 * 60 * 1000,
    ...options,
  });
}

/**
 * Fetch all patient weight / chart data points for doctor & patient.
 */
export function usePatientWeights(doctorId, patientId, options = {}) {
  return useQuery({
    queryKey: queryKeys.charts.patientWeights(doctorId, patientId),
    queryFn: async ({ signal }) => {
      const res = await listPatientWeights(doctorId, patientId, { signal });
      return normalizeWeightResponse(res);
    },
    enabled: Boolean(doctorId && patientId),
    // Add/delete mutations invalidate this authoritative cache explicitly.
    staleTime: Infinity,
    gcTime: Infinity,
    ...options,
  });
}

/**
 * Fetch patient weights / chart data points scoped to a specific appointment.
 */
export function useAppointmentWeights(doctorId, patientId, appointmentId, options = {}) {
  return useQuery({
    queryKey: queryKeys.charts.appointmentWeights(doctorId, patientId, appointmentId),
    queryFn: async ({ signal }) => {
      const res = await listPatientWeightss(doctorId, patientId, appointmentId, { signal });
      return normalizeWeightResponse(res);
    },
    enabled: Boolean(doctorId && patientId && appointmentId),
    staleTime: Infinity,
    gcTime: Infinity,
    ...options,
  });
}

/**
 * Fetch flattened patient chart points across all properties.
 */
export function usePatientPoints(doctorId, patientId, options = {}) {
  return useQuery({
    queryKey: queryKeys.charts.patientPoints(doctorId, patientId),
    queryFn: async ({ signal }) => {
      const res = await fetchChartssList(doctorId, patientId, { signal });
      return flattenPropertyPoints(Array.isArray(res) ? res : res?.data ?? []);
    },
    enabled: Boolean(doctorId && patientId),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    ...options,
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Mutation to create a new chart property.
 */
export function useCreateChartProperty(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ property, doctorId, patientId }) => {
      return createChartObservation(property, doctorId, patientId);
    },
    onSuccess: (data, variables) => {
      if (variables?.doctorId && variables?.patientId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.charts.properties(variables.doctorId, variables.patientId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.charts.catalog(),
      });
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * Mutation to delete a chart property.
 */
export function useDeleteChartProperty(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ propertyId, doctorId, patientId }) => {
      return deleteChartObservation(propertyId, doctorId, patientId);
    },
    onSuccess: (data, variables) => {
      if (variables?.doctorId && variables?.patientId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.charts.properties(variables.doctorId, variables.patientId),
        });
      }
      queryClient.invalidateQueries({
        queryKey: queryKeys.charts.catalog(),
      });
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * Mutation to add a chart data point.
 */
export function useAddChartPoint(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ doctorId, patientId, payload }) => {
      return await addWeightApi(doctorId, patientId, payload);
    },
    onSuccess: (data, variables) => {
      const { doctorId, patientId } = variables || {};
      if (doctorId && patientId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.charts.patientWeights(doctorId, patientId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.charts.patientPoints(doctorId, patientId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.charts.properties(doctorId, patientId),
        });
      }
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}

/**
 * Mutation to delete a chart data point.
 */
export function useDeleteChartPoint(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ pointId, doctorId, patientId }) => {
      return deleteWeightApi(pointId);
    },
    onSuccess: (data, variables) => {
      const { doctorId, patientId } = variables || {};
      if (doctorId && patientId) {
        queryClient.invalidateQueries({
          queryKey: queryKeys.charts.patientWeights(doctorId, patientId),
        });
        queryClient.invalidateQueries({
          queryKey: queryKeys.charts.patientPoints(doctorId, patientId),
        });
      } else {
        queryClient.invalidateQueries({
          queryKey: queryKeys.charts.all,
        });
      }
      options.onSuccess?.(data, variables);
    },
    ...options,
  });
}
